import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import yup from '../../core/utils/yupValidate';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NotFoundScreen from '../404/NotFoundScreen';
import { LOGIN_TYPE, MSG, ROLE, STUDENT_STATUS, SYSTEM_PATH, USER_TYPE, USER_TYPE_STRING } from '../../core/configs/constants';
import { useStore } from '../../core/utils/hook';
import './style.scss';
import { ArrowLeft } from 'lucide-react';


const SetPasswordSyncScreen = observer((props) => {
    const {
        authStore: { clearAuthenticationWithSetPassword },
        passwordStore: { checkUuid, setPasswordSync },
        modalStore: { openAlert }
    } = useStore();

    const [noUuid, setNoUuid] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showPassword, setShowPassword] = useState({
        password: false,
        passwordConfirm: false
    });
    const [userRole, setUserRole] = useState(LOGIN_TYPE.STUDENT);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [roleReg, setRoleReg] = useState(ROLE.STD_WAIT_SELECT);

    useEffect(() => {
        const header = document.getElementById('header-site');
        if (!header) return;

        const updateHeight = () => {
            setHeaderHeight(header.offsetHeight);
        };

        // Follow initial height header
        updateHeight();

        // Follow resize header
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(header);

        // Follow resize window
        window.addEventListener('resize', updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');

    const validateSetPasswordSchema = yup.object().shape({
        password: yup
            .string()
            .required(MSG['error.required'])
            .matches(
                new RegExp('^(?=.*[A-Z])[a-zA-Z0-9]{8,}$'),
                MSG['error.password_format_detail']
            ),
        passwordConfirm: yup
            .string()
            .oneOf([yup.ref('password')], MSG['error.confirm_password_not_match'])
            .required(MSG['error.required']),
        roleId: roleReg == ROLE.STD_WAIT_SELECT ?
            yup.number().required(MSG['error.required']) :
            yup.number().optional().nullable(),
        examCode: yup.string().when('roleId', {
            is: val => val === USER_TYPE.STUDENT_GROUP,
            then: schema => schema.required(MSG['error.required']),
            otherwise: schema => schema.nullable().optional()
        })
    });

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
        trigger,
        getValues, setValue, reset
    } = useForm({
        resolver: yupResolver(validateSetPasswordSchema),
        mode: 'onChange'
    });

    const password = watch('password', '');
    const passwordConfirm = watch('passwordConfirm', '');
    const roleId = watch('roleId', '');
    const examCode = watch('examCode', '');

    const navigate = useNavigate();

    const toggleShowPassword = (state) => {
        setShowPassword({
            ...showPassword,
            [state]: !showPassword[state]
        });
    };

    React.useEffect(() => {
        clearAuthenticationWithSetPassword();
    }, [])

    React.useEffect(() => {
        if (password?.length > 0 && passwordConfirm !== '') {
            trigger('passwordConfirm');
        }
    }, [password])

    const onSetPass = async (data) => {
        const payload = {
            uuid,
            password: data.password,
            passwordConfirm: data.passwordConfirm,
            ...roleReg === ROLE.STD_WAIT_SELECT && {
                roleId: data.roleId,
                ...data.roleId === ROLE.ROLE_STUDENT && { examCode: data?.examCode }
            }
        }
        const res = await setPasswordSync(payload);
        if (res) {
            // Held for approval → tell the learner before sending them to login (same as self-registration).
            if (res.status === STUDENT_STATUS.PENDING_APPROVAL) {
                openAlert(
                    'ご登録いただきありがとうございます。\n現在、ご所属法人管理者様の承認待ちの状態となります。\n管理者様の承認が完了し次第、メールにてお知らせいたします。',
                    () => navigate(SYSTEM_PATH.LOGIN)
                );
            } else {
                navigate(SYSTEM_PATH.LOGIN);
            }
        }
    };

    React.useEffect(() => {
        handleCheckUuid();
    }, []);

    React.useEffect(() => {
        if (roleReg === ROLE.STD_WAIT_SELECT) {
            setValue('roleId', USER_TYPE.STUDENT_INDIVIDUAL);
        }
    }, [roleReg]);

    const handleCheckUuid = async () => {
        try {
            const res = await checkUuid({ uuid });
            if (res?.ok) {
                if (res?.data?.role === USER_TYPE.GROUP_MANAGER) {
                    setUserRole(LOGIN_TYPE.GROUP_ADMIN)
                } else {
                    setUserRole(LOGIN_TYPE.STUDENT)
                }
                setRoleReg(res?.data?.role);
            } else if (res?.data?.isImport) {
                // Expired import link: render the NotFound screen like the other invalid
                // cases (no form leaks), plus a dedicated alert; go home when it is closed.
                setNoUuid(true);
                // Left-align this long expired-link message instead of the alert's default center
                openAlert(
                    <span style={{ display: 'block', textAlign: 'left' }}>{res?.message}</span>,
                    () => navigate(SYSTEM_PATH.HOME)
                );
            } else {
                setNoUuid(true);
            }
            setLoading(false);
        } catch (error) {
            console.log('error: ', error);
            setNoUuid(true);
            setLoading(false);
        }
    };

    const onChangeUserType = (event) => {
        const role = event.target.value;
        setValue('roleId', role);
        if (role == USER_TYPE.STUDENT_INDIVIDUAL) {
            setValue('examCode', '');
        }
    }

    if (loading) return null;

    if (!uuid || noUuid) return (
        <div style={{ margin: '50px auto', textAlign: 'center' }}>
            <NotFoundScreen />
        </div>
    )

    return (
        <div id="main" className="wrapper main-signin main-ChangPassword">
            <div className="content-signin">
                <div className="container" style={{ width: '100%' }}>
                    <div className="content-form" style={{ width: '70%', justifySelf: 'center' }}>
                        <h2 className="title-primary" style={{ lineHeight: '40px' }}>
                            同期情報の入力を完了してください
                        </h2>
                        <p className="title-primary-setPass" style={{ marginTop: '32px' }}>アカウント同期を完了するため、以下の情報をご入力ください。</p>

                        <form onSubmit={handleSubmit(onSetPass)}>
                            <div className="item">
                                <label htmlFor="">
                                    ログインパスワード: <span>*</span>
                                </label>
                                <div className="input">
                                    <input
                                        id="password"
                                        type={!showPassword.password ? 'password' : 'text'}
                                        {...register('password')}
                                        autoComplete="off"
                                        onCopy={(e) => e.preventDefault()}
                                        onPaste={(e) => e.preventDefault()}
                                        onCut={(e) => e.preventDefault()}
                                    />
                                    <button
                                        type="button"
                                        style={{ zIndex: 10 }}
                                        onClick={() => toggleShowPassword('password')}
                                    >
                                        {!showPassword.password ? (
                                            <i className="fa fa-eye-slash"></i>
                                        ) : (
                                            <i className="fa fa-eye"></i>
                                        )}
                                    </button>
                                </div>
                                {errors?.password && (
                                    <div className="text-danger mg-t-5">
                                        {errors.password?.message}
                                    </div>
                                )}
                            </div>
                            <div className="item">
                                <label htmlFor="passwordConfirm">
                                    ログインパスワード確認: <span>*</span>
                                </label>
                                <div className="input">
                                    <input
                                        id="passwordConfirm"
                                        type={!showPassword.passwordConfirm ? 'password' : 'text'}
                                        {...register('passwordConfirm')}
                                        autoComplete="off"
                                        onCopy={(e) => e.preventDefault()}
                                        onPaste={(e) => e.preventDefault()}
                                        onCut={(e) => e.preventDefault()}
                                    />
                                    <button
                                        type="button"
                                        style={{ zIndex: 10}}
                                        onClick={() => toggleShowPassword('passwordConfirm')}
                                    >
                                        {!showPassword.passwordConfirm ? (
                                            <i className="fa fa-eye-slash"></i>
                                        ) : (
                                            <i className="fa fa-eye"></i>
                                        )}
                                    </button>
                                </div>
                                {errors?.passwordConfirm && (
                                    <div className="text-danger mg-t-5">
                                        {errors.passwordConfirm?.message}
                                    </div>
                                )}
                            </div>
                            {roleReg == ROLE.STD_WAIT_SELECT && (
                                <div className="item ">
                                    <label for="">ユーザーの種類 <span>*</span></label>
                                    <div className="input"
                                        style={{
                                            display: 'flex', border: '1px', borderColor: '#D9D9D9',
                                            borderStyle: 'solid', borderRadius: '4px', alignItems: 'center',
                                        }}>
                                        <ul style={{ display: 'flex', marginTop: '12px' }}>
                                            <input checked={getValues('roleId') == USER_TYPE.STUDENT_INDIVIDUAL} type="radio" {...register('roleId', {
                                                onChange: onChangeUserType
                                            })} value={USER_TYPE.STUDENT_INDIVIDUAL} id='individualStudent' />
                                            <label style={{ marginLeft: '12px' }}
                                                role='button' htmlFor='individualStudent'>{USER_TYPE_STRING.STUDENT_INDIVIDUAL}</label>
                                        </ul>
                                        <ul style={{ display: 'flex', marginTop: '12px' }}>
                                            <input checked={getValues('roleId') == USER_TYPE.STUDENT_GROUP} type="radio" {...register('roleId', {
                                                onChange: onChangeUserType
                                            })} value={USER_TYPE.STUDENT_GROUP} id='groupStudent' />
                                            <label style={{ marginLeft: '12px' }}
                                                role='button' htmlFor='groupStudent'>{USER_TYPE_STRING.STUDENT_GROUP}</label>
                                        </ul>
                                    </div>
                                    {errors?.roleId?.message && <p className='text-error'>{errors?.roleId?.message}</p>}
                                </div>
                            )}
                            {roleReg == ROLE.STD_WAIT_SELECT && getValues('roleId') == USER_TYPE.STUDENT_GROUP && (
                                <div className="item">
                                    <label htmlFor="examCode">
                                        受講コード:
                                        <span> *</span>
                                    </label>
                                    <div className="input">
                                        <input
                                            id="examCode"
                                            type={'text'}
                                            {...register('examCode')}
                                            autoComplete="off"
                                            onCopy={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                        />
                                    </div>
                                    {errors?.examCode && (
                                        <div className="text-danger mg-t-5">
                                            {errors.examCode?.message}
                                        </div>
                                    )}
                                </div>
                            )}
                            <input type="submit" value="確認" />
                        </form>
                        <div
                            style={{
                                justifySelf: 'center',
                                alignItems: 'center',
                                marginTop: '32px',
                                cursor: 'pointer',
                                color: '#1B1B1B'
                            }}
                            onClick={() => navigate(SYSTEM_PATH.HOME)}
                        >
                            <ArrowLeft size={16} style={{ marginRight: '12px' }} />
                            ホームページに戻る
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default SetPasswordSyncScreen;
